/**
 * Culians — Cookie Banner
 *
 * Slides up from the bottom on first visit, lets the customer
 * Accept or Decline tracking. Choice is persisted in
 * localStorage so the banner doesn't re-appear on subsequent
 * visits.
 *
 * Hooks into Shopify's Customer Privacy API so the actual
 * consent state (analytics / marketing / preferences /
 * sale_of_data) is set correctly — keeping us compliant
 * regardless of what UI is shown.
 *
 * IMPORTANT: Shopify's default cookie banner must be turned
 * OFF in the admin (Settings → Customer privacy → Cookie
 * banner) so both banners don't show. The Customer Privacy
 * API still functions when the default UI is disabled.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'culians_cookie_consent';
  // 180 days — after this the banner re-prompts (best practice
  // under GDPR / CCPA: consent shouldn't be permanent).
  var CONSENT_TTL_MS = 180 * 24 * 60 * 60 * 1000;
  // ~400ms after DOM ready before sliding the banner in. Lets the
  // page paint first so the slide-up reads as a deliberate moment.
  var REVEAL_DELAY_MS = 400;

  function getBanner() {
    return document.getElementById('culians-cookie');
  }

  /* ─── Open / close ─────────────────────────────────────── */

  function open() {
    var b = getBanner();
    if (!b) return;
    b.setAttribute('data-open', 'true');
    b.setAttribute('aria-hidden', 'false');
  }

  function close() {
    var b = getBanner();
    if (!b) return;
    b.setAttribute('data-open', 'false');
    b.setAttribute('aria-hidden', 'true');
  }

  /* ─── Persisted consent ────────────────────────────────── */

  function readConsent() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.timestamp !== 'number') return null;
      // Expire stale consent so we re-prompt periodically.
      if (Date.now() - parsed.timestamp > CONSENT_TTL_MS) return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function writeConsent(accepted) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        accepted: !!accepted,
        timestamp: Date.now()
      }));
    } catch (e) { /* quota / private mode — fine to ignore */ }
  }

  /* ─── Shopify Customer Privacy API ─────────────────────── */
  /* The actual consent state for analytics, marketing, etc., is
   * stored by Shopify. We use their API so trackers respect the
   * choice. The API may not be loaded yet on early page paint,
   * so we re-try briefly. */

  function applyToShopify(accepted) {
    function call() {
      if (!window.Shopify || !window.Shopify.customerPrivacy ||
          typeof window.Shopify.customerPrivacy.setTrackingConsent !== 'function') {
        return false;
      }
      var consent = {
        analytics: accepted,
        marketing: accepted,
        preferences: accepted,
        sale_of_data: accepted
      };
      try {
        window.Shopify.customerPrivacy.setTrackingConsent(consent, function () { /* noop */ });
        return true;
      } catch (err) {
        console.error('Culians cookie — customerPrivacy error:', err);
        return false;
      }
    }

    if (call()) return;
    // Retry briefly while the API loads (rare in practice but
    // safe to handle).
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      if (call() || attempts > 20) clearInterval(timer);
    }, 250);
  }

  /* ─── Handlers ─────────────────────────────────────────── */

  function accept() {
    writeConsent(true);
    applyToShopify(true);
    close();
  }

  function decline() {
    writeConsent(false);
    applyToShopify(false);
    close();
  }

  /* ─── Footer "Cookie Preferences" link ────────────────────
   * Adds a small link next to the existing footer "Cookie Policy"
   * link that re-opens this banner. Required in some EU
   * jurisdictions, good practice everywhere — lets a returning
   * customer change their mind without clearing cookies manually.
   *
   * Why JS injection: the footer is composed from theme-editor
   * blocks (a Shopify menu rendered via Atelier's footer.liquid),
   * so editing it from code would mean either touching Atelier
   * theme files or asking the merchant to add a menu item. JS
   * injection sidesteps both — finds the live Cookie Policy link
   * and inserts a sibling that looks identical and calls reset().
   *
   * Robustness: the selector intentionally does NOT require the
   * link to live inside a <footer> tag — Atelier's section wrapper
   * isn't always a real <footer> element, and we still want to
   * find the link in that case. We do exclude links inside our own
   * cookie banner so we don't accidentally inject next to the
   * banner's own Cookie Policy link.
   */
  function findFooterCookieLink() {
    var candidates = document.querySelectorAll(
      'a[href*="/pages/cookie-policy"], a[href*="cookie-policy"], a[href*="cookie_policy"]'
    );
    for (var i = 0; i < candidates.length; i += 1) {
      // Skip the cookie banner's own internal Cookie Policy link.
      if (candidates[i].closest('#culians-cookie')) continue;
      return candidates[i];
    }
    return null;
  }

  function addFooterPreferencesLink() {
    // Don't inject twice — covers theme-editor section reloads.
    if (document.querySelector('[data-culians-cookie-prefs]')) return true;

    var existingLink = findFooterCookieLink();
    if (!existingLink) return false;

    // Clone the wrapping element (typically <li>) so spacing /
    // typography exactly match the other policy links — those
    // styles come from the parent menu's CSS.
    var sourceItem = existingLink.parentElement;
    if (!sourceItem) return false;

    var newItem = document.createElement(sourceItem.tagName.toLowerCase());
    newItem.className = sourceItem.className;

    var newLink = document.createElement('a');
    newLink.href = '#cookie-preferences';
    newLink.textContent = 'Cookie Preferences';
    newLink.className = existingLink.className;
    newLink.setAttribute('data-culians-cookie-prefs', '');
    newLink.addEventListener('click', function (e) {
      e.preventDefault();
      if (window.CulianCookie && typeof window.CulianCookie.reset === 'function') {
        window.CulianCookie.reset();
      }
    });

    newItem.appendChild(newLink);
    sourceItem.insertAdjacentElement('afterend', newItem);
    return true;
  }

  /* Retry up to 5 times at 500ms intervals if the footer hasn't
   * rendered yet on first try. Covers themes that lazy-render the
   * footer or wrap it in a custom component. Bails as soon as one
   * attempt succeeds. */
  function tryInjectFooterLink(attempts) {
    attempts = attempts || 0;
    if (addFooterPreferencesLink()) return;
    if (attempts >= 5) {
      console.warn(
        'Culians cookie: Cookie Policy link not found in footer after ' +
        (attempts + 1) + ' attempts — Cookie Preferences link not injected.'
      );
      return;
    }
    setTimeout(function () { tryInjectFooterLink(attempts + 1); }, 500);
  }

  /* ─── Init ──────────────────────────────────────────────── */

  function init() {
    var banner = getBanner();
    if (!banner) return;

    // Wire actions (delegated so the banner can be replaced
    // later by section reload without breaking).
    banner.addEventListener('click', function (e) {
      if (e.target.closest('[data-culians-cookie-accept]')) {
        e.preventDefault();
        accept();
      } else if (e.target.closest('[data-culians-cookie-decline]')) {
        e.preventDefault();
        decline();
      }
    });

    // Footer preferences link — added once per page load, with
    // retries in case the footer renders late.
    tryInjectFooterLink();

    // Only reveal if no fresh consent is on file. Slide in after
    // a short delay so the page paints first.
    if (!readConsent()) {
      setTimeout(open, REVEAL_DELAY_MS);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after Shopify section re-renders (theme editor).
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.detail) return;
    if (e.detail.sectionId === 'culians-cookie-banner') {
      init();
    }
    // Footer was re-rendered — re-inject the preferences link.
    if (/footer/i.test(e.detail.sectionId || '')) {
      addFooterPreferencesLink();
    }
  });

  // Expose for future integrations / a "Reset cookie preferences"
  // link in the footer.
  window.CulianCookie = {
    open: open,
    close: close,
    accept: accept,
    decline: decline,
    reset: function () {
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
      open();
    }
  };
})();
